import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import { SmsLinkCallback } from "./pages/authentication/SmsLinkCallback";
import { AuthProfilePrompt } from "./pages/authentication/components/AuthProfilePrompt";
import React, { Suspense } from "react";
import { ChakraProvider, Center, Image } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import SecurityPage from "./pages/security/SecurityPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import Podcast from "./pages/Podcast";
import ClubInABox from "./pages/ClubInABox";
import PokerInABox from "./pages/PokerInABox";
import PitchDeckPage from "./pages/PitchDeckPage";
import WhitePaperPage from "./pages/WhitePaperPage";
import TimelinePage from "./pages/TimelinePage";
import TokenomicsPage from "./pages/TokenomicsPage";
import TokenSalePage from "./pages/TokenSalePage";
import ValidatorDelegatorPage from "./pages/ValidatorDelegatorPage";
import Resources from "./pages/Resources";

// Learn pages
import WhatIsBlock52 from "./pages/learn/WhatIsBlock52";
import PokerVirtualMachine from "./pages/learn/PokerVirtualMachine";
import WhatIsB52Token from "./pages/learn/WhatIsB52Token";
import Block52Wallets from "./pages/learn/Block52Wallets";
import SmartContracts from "./pages/learn/SmartContracts";
import GasFees from "./pages/learn/GasFees";
import RunANode from "./pages/learn/RunANode";
import SecurityScamPrevention from "./pages/learn/SecurityScamPrevention";
import Block52Glossary from "./pages/learn/Block52Glossary";

// Legal pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import CookiePolicy from "./pages/CookiePolicy";

// About pages
import Careers from "./pages/about/Careers";

// Authentication
import { LoginPage } from "./pages/authentication";

import OneGroupProposal from "./pages/offers/onegroup";
import WattDropProposal from "./pages/offers/wattdrop";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations
import clientsModuleConfig from "./pages/clients/moduleConfig";
import governanceModuleConfig from "./pages/governance/moduleConfig";
import { governancePreviewForPath } from "./pages/governance/previews";

/**
 * A module may hand a signed-out visitor something to READ before the sign-in box (2026-09-16;
 * `seamless-page-auth-pattern.md` Part 3). The teaser is written by the module and public-safe — it
 * never imports a page component, so nothing protected reaches the browser. Without this registry the
 * modal opens over a blank page, which is what every tenant did until now.
 */
const ROUTE_PREVIEWS: Record<string, (path: string) => { title: string; blurb: string; node?: React.ReactNode } | undefined> = {
  governance: governancePreviewForPath,
};
import companiesModuleConfig from "./pages/companies/moduleConfig";
import CompaniesList from "./pages/companies/CompaniesList";
import NewCompany from "./pages/companies/NewCompany";
import CompanyDetails from "./pages/companies/CompanyDetails";
import EditCompany from "./pages/companies/EditCompany";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import companyKnowledgeModuleConfig from "./pages/companyknowledge/moduleConfig";
import frontendUpgradesModuleConfig from "./pages/frontend-upgrades/moduleConfig";
import seoMetadataModuleConfig from "./pages/seometadata/moduleConfig";
import mediaCdnModuleConfig from "./pages/media-cdn/moduleConfig";
import rewardsModuleConfig from "./pages/rewards/moduleConfig";
import analyticsModuleConfig from "./pages/analytics/moduleConfig";

/*
 * MIGRATION NOTE: We are transitioning to a dynamic module routing system.
 *
 * The Clients module has been converted as the example pattern.
 * As we update each module, we will:
 * 1. Create/update its moduleConfig.ts file (see clients/moduleConfig.ts as example)
 * 2. Import the moduleConfig here
 * 3. Add it to the modules array below
 * 4. Remove its hardcoded routes from below
 *
 * This allows modules to be truly plug-and-play with automatic route registration
 * and centralized permission management.
 */

import billsModuleConfig from "./pages/bills/moduleConfig";
import adminModuleConfig from "./pages/admin/moduleConfig";

// Combine all module configurations

/**
 * Hand-written routes (the ones not owned by a module) get their tab name here — the standard's
 * `<Page> | <Site>`, with the site from brandConfig. Module routes need nothing: DocumentTitleManager
 * derives them from each moduleConfig. Never write document.title from a page (2026-09-16).
 */
const STATIC_ROUTE_TITLES: Record<string, string> = {
  "/": "",
  "/login": "Sign in",
  "/auth/sms-link": "Signing in",
  "/auth/email-link": "Signing in",
  "/security": "Security",
  "/onboarding": "Onboarding",
  "/terms": "Terms",
  "/privacy": "Privacy",
};
const modules = [
  governanceModuleConfig, // Groups that decide things together — motions, votes, work, treasury, a permanent record
  clientsModuleConfig,
  companiesModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  billsModuleConfig,
  emailsModuleConfig,
  companyKnowledgeModuleConfig,
  adminModuleConfig,
  seoMetadataModuleConfig,
  mediaCdnModuleConfig,
  rewardsModuleConfig,
  analyticsModuleConfig,
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModulePageLayout } from "./components/ModulePageLayout";

// Pulse animation for loading icon
const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.95); }
`;

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh" bg={brandConfig.colors.background.main}>
    <Image
      src="/b52favicon.svg"
      alt="Loading..."
      boxSize="64px"
      animation={`${pulseAnimation} 1.5s ease-in-out infinite`}
    />
  </Center>
);

// Bill-related imports (moved to dynamic routing via billsModuleConfig)
// Special bill pages that aren't in the module
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
import BillChaserPage from "./pages/admin/billChaserPage";

// Calendar-related imports
import CalendarAccountsAdmin from "./pages/calendars/CalendarAccountsAdmin";

// Subscription/Billing-related imports
import SubscriptionsDashboard from "./pages/subscriptions/SubscriptionsDashboard";
import ManageSubscriptions from "./pages/subscriptions/ManageSubscriptions";
import PaymentMethods from "./pages/subscriptions/PaymentMethods";
import InvoiceHistory from "./pages/subscriptions/InvoiceHistory";
import BillingSettings from "./pages/subscriptions/BillingSettings";
import CreateSubscription from "./pages/subscriptions/CreateSubscription";
import ManualCharging from "./pages/subscriptions/ManualCharging";
import EditSubscription from "./pages/subscriptions/EditSubscription";

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";

// Event imports
import Event1Page from "./pages/events/tom/1";
import Event2Page from "./pages/events/tom/2";
import Event3Page from "./pages/events/tom/3";
import Event4Page from "./pages/events/tom/4";
import Block52EventPage from "./pages/events/tom/Block52";
import ChrisEventPage from "./pages/events/tom/chris";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";

// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";
import ShippingAddresses from "./pages/profile/ShippingAddresses";
import ProfilePaymentMethods from "./pages/profile/PaymentMethods";
import SubscriptionOffers from "./pages/profile/SubscriptionOffers";
import CurrentSubscriptions from "./pages/profile/CurrentSubscriptions";

// Permissions Matrix import
import Permissions from "./pages/Permissions";

// Offers module imports
import { MaintenanceOffer } from "./pages/offers/onegroup/maintenance";
import SiteMaintenance from "./pages/offers/onegroup/sitemaintenance";

// Cheatsheet imports
import CheatsheetIndex from "./pages/cheatsheets/index";
import LocalPvmDebugSetup from "./pages/cheatsheets/LocalPvmDebugSetup";

// Public Booking imports (NO AUTH REQUIRED)
import PublicBookingPage from "./pages/calendars/booking/PublicBookingPage";
import PublicBookingEventPage from "./pages/calendars/booking/PublicBookingEventPage";
import PublicBookingConfirmPage from "./pages/calendars/booking/PublicBookingConfirmPage";
import PublicBookingSuccessPage from "./pages/calendars/booking/PublicBookingSuccessPage";
import ManageBooking from "./pages/calendars/ManageBooking";

// Business Builder imports (NO AUTH REQUIRED - Public Pages)
import BusinessBuilder from "./pages/businessbuilder/index";
import Tokenomics from "./pages/businessbuilder/Tokenomics";
import Governance from "./pages/businessbuilder/Governance";
import Treasury from "./pages/businessbuilder/Treasury";
import Marketplace from "./pages/businessbuilder/Marketplace";
import TithingCalculator from "./pages/businessbuilder/TithingCalculator";

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { DocumentTitleManager } from "./components/DocumentTitleManager";
import { ScrollToTop } from "./components/ScrollToTop";
import { useAuth } from "./contexts/AuthContext";

const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <>
      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar />}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <DocumentTitleManager modules={modules} staticTitles={STATIC_ROUTE_TITLES}>
      <Routes>
        <Route path="/auth/email-link" element={<EmailLinkCallback />} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Security & sign-in management — any logged-in user */}
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />

        {/* Onboarding — staff with the ONBOARDING permission */}
        <Route path="/onboarding" element={<ProtectedRoute requiredPermissions={["ONBOARDING"]}><OnboardingPage /></ProtectedRoute>} />

        {/* Updates page (formerly Podcast) */}
        <Route path="/updates" element={<Podcast />} />

        {/* Club-in-a-Box page */}
        <Route path="/club-in-a-box" element={<ClubInABox />} />

        {/* Poker-in-a-Box page */}
        <Route path="/p-in-a-box" element={<PokerInABox />} />

        {/* Pitch Deck page */}
        <Route path="/pitch-deck" element={<PitchDeckPage />} />

        {/* White Paper page */}
        <Route path="/white-paper" element={<WhitePaperPage />} />

        {/* Timeline page */}
        <Route path="/timeline" element={<TimelinePage />} />

        {/* Tokenomics page */}
        <Route path="/tokenomics" element={<TokenomicsPage />} />

        {/* Token Sale page */}
        <Route path="/token-sale" element={<TokenSalePage />} />

        {/* Validator/Delegator Calculator page */}
        <Route path="/validator-delegator" element={<ValidatorDelegatorPage />} />

        {/* Resources page */}
        <Route path="/resources" element={<Resources />} />

        {/* Learn pages */}
        <Route path="/learn/what-is-block52" element={<WhatIsBlock52 />} />
        <Route path="/learn/poker-virtual-machine" element={<PokerVirtualMachine />} />
        <Route path="/learn/what-is-b52-token" element={<WhatIsB52Token />} />
        <Route path="/learn/block52-wallets" element={<Block52Wallets />} />
        <Route path="/learn/smart-contracts" element={<SmartContracts />} />
        <Route path="/learn/gas-fees" element={<GasFees />} />
        <Route path="/learn/run-a-node" element={<RunANode />} />
        <Route path="/learn/security-scam-prevention" element={<SecurityScamPrevention />} />
        <Route path="/learn/block52-glossary" element={<Block52Glossary />} />

        {/* Legal pages */}
        <Route path="/pp" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/cookie" element={<CookiePolicy />} />

        {/* About pages */}
        <Route path="/about/careers" element={<Careers />} />

        {/* Admin Login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* 🚀 BUSINESS BUILDER PAGES (NO AUTH REQUIRED - Public Pages) */}
        <Route path="/businessbuilder" element={<BusinessBuilder />} />
        <Route path="/businessbuilder/tokenomics" element={<Tokenomics />} />
        <Route path="/businessbuilder/governance" element={<Governance />} />
        <Route path="/businessbuilder/treasury" element={<Treasury />} />
        <Route path="/businessbuilder/marketplace" element={<Marketplace />} />
        <Route path="/businessbuilder/tithing-calculator" element={<TithingCalculator />} />

        {/* 📅 PUBLIC BOOKING PAGES (NO AUTH REQUIRED) */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/book/:slug/:eventTypeId" element={<PublicBookingEventPage />} />
        <Route path="/book/:slug/confirm" element={<PublicBookingConfirmPage />} />
        <Route path="/book/:slug/success" element={<PublicBookingSuccessPage />} />
        <Route path="/calendars/booking/manage/:token" element={<ManageBooking />} />

        {/* Public Proposal Pages */}
        <Route path="/offers/onegroup" element={<OneGroupProposal />} />
        <Route path="/offers/wattdrop" element={<WattDropProposal />} />

        {/* Event routes */}
        <Route path="/events/tom/1" element={<Event1Page />} />
        <Route path="/events/tom/2" element={<Event2Page />} />
        <Route path="/events/tom/3" element={<Event3Page />} />
        <Route path="/events/tom/4" element={<Event4Page />} />
        <Route path="/events/tom/block52" element={<Block52EventPage />} />
        <Route path="/events/tom/chris" element={<ChrisEventPage />} />

        {/* Dynamic Module Routes */}
        {modules.map((module) =>
          module.routes?.map((route) => {
            const useLayout = (route as any).useLayout === true;
            const layoutOptions = (route as any).layoutOptions;
            const innerComponent = (
              <Suspense fallback={<LoadingFallback />}>
                <route.component />
              </Suspense>
            );
            const pageComponent = useLayout ? (
              <ModulePageLayout
                moduleConfig={module as any}
                layoutOptions={layoutOptions}
              >
                {innerComponent}
              </ModulePageLayout>
            ) : (
              innerComponent
            );
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  (route as any).public ? (
                    pageComponent
                  ) : (
                    <ProtectedRoute requiredPermissions={route.permissions || []} preview={ROUTE_PREVIEWS[module.id]?.(route.path)}>
                      {pageComponent}
                    </ProtectedRoute>
                  )
                }
              />
            );
          })
        )}

        {/* Special bill routes not in the module */}
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        <Route path="/bill-chaser" element={<BillChaserPage />} />

        {/* Company routes */}
        <Route path="/companies" element={<CompaniesList />} />
        <Route path="/companies/new" element={<NewCompany />} />
        <Route path="/companies/:id" element={<CompanyDetails />} />
        <Route path="/companies/edit/:id" element={<EditCompany />} />

        {/* Calendar routes */}
        <Route path="/calendar/admin/calendars" element={<CalendarAccountsAdmin />} />

        {/* Permissions Matrix */}
        <Route path="/permissions" element={<Permissions />} />

        {/* Subscription/Billing routes */}
        <Route path="/subscriptions" element={<SubscriptionsDashboard />} />
        <Route path="/subscriptions/manage" element={<ManageSubscriptions />} />
        <Route path="/subscriptions/create" element={<CreateSubscription />} />
        <Route path="/subscriptions/payment-methods" element={<PaymentMethods />} />
        <Route path="/subscriptions/invoices" element={<InvoiceHistory />} />
        <Route path="/subscriptions/settings" element={<BillingSettings />} />
        <Route path="/subscriptions/manual-charging" element={<ManualCharging />} />
        <Route path="/subscriptions/:id/edit" element={<EditSubscription />} />

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />
        <Route path="/profile/edit" element={<EditProfile />} />
        <Route path="/profile/settings" element={<Settings />} />
        <Route path="/profile/shipping" element={<ShippingAddresses />} />
        <Route path="/profile/payment-methods" element={<ProfilePaymentMethods />} />
        <Route path="/profile/subscription-offers" element={<SubscriptionOffers />} />
        <Route path="/profile/subscriptions" element={<CurrentSubscriptions />} />

        {/* Offers routes */}
        <Route path="/offers/maintenance" element={<MaintenanceOffer />} />
        <Route path="/offers/sitemaintenance" element={<SiteMaintenance />} />

        {/* Cheatsheet routes */}
        <Route path="/cheatsheets" element={<CheatsheetIndex />} />
        <Route path="/cheatsheets/local-pvm-debug" element={<LocalPvmDebugSetup />} />
      </Routes>
      </DocumentTitleManager>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ChakraProvider theme={theme}>
              <AuthProfilePrompt />
                    <AppContent />
            </ChakraProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
